import { useState } from 'react'
import type { ReactNode } from 'react'
import './ModalBackdrop.css'

/** Scrim weight. 'utility' — the default — still clearly dims the page but keeps
 *  the context behind readable, which is useful for pickers and confirms. 'immersive' is one step
 *  heavier, for modals whose content is the whole point and where what's behind is irrelevant. */
export type ModalScrim = 'utility' | 'immersive'

/** How the panel arrives. 'standard' is the app's snap — right for something the user just tapped
 *  open. 'calm' is slower on the way in, for a modal that appears on its own: nobody asked for it,
 *  so it eases onto the screen rather than snapping in front of what they were reading. */
export type ModalPace = 'standard' | 'calm'

interface ModalBackdropProps {
  /** Either plain content, or a render function handed the backdrop's own
   *  `close` — the way a child (a close button, a picker committing a
   *  selection) plays the exit animation instead of running a parallel timer of
   *  its own. Pass an `after` callback to run once the animation
   *  has finished, alongside `onClose`. */
  children: ReactNode | ((close: (after?: () => void) => void) => ReactNode)
  onClose: () => void
  scrim?: ModalScrim
  pace?: ModalPace
  /** Set when something else owns the entrance — the gameweek picker's shared-element expand.
   * The content's rise/scale-in is dropped for a plain fade so the box doesn't
   *  animate against the flying clone, and so the destination can be measured at its final
   *  position rather than mid-animation. */
  staticContentEntry?: boolean
}

// Matches each pace's exit transition below, plus a small buffer, so the backdrop and content have
// finished animating out before the parent actually unmounts them.
const EXIT_DURATION_MS: Record<ModalPace, number> = { standard: 180, calm: 260 }

function ModalBackdrop({
  children,
  onClose,
  scrim = 'utility',
  pace = 'standard',
  staticContentEntry = false,
}: ModalBackdropProps) {
  const [isClosing, setIsClosing] = useState(false)

  // `after` runs just before `onClose` once the exit animation is done, so a
  // child can commit something (a picked team/match) on the same close.
  // Re-entrant calls are dropped: a click that lands during the animation must
  // not queue a second onClose, nor a selection the user has already dismissed.
  const handleClose = (after?: () => void) => {
    if (isClosing) return
    setIsClosing(true)
    setTimeout(() => {
      after?.()
      onClose()
    }, EXIT_DURATION_MS[pace])
  }

  return (
    <div
      className={`modal-backdrop${scrim === 'immersive' ? ' modal-backdrop--immersive' : ''}${
        pace === 'calm' ? ' modal-backdrop--calm' : ''
      } ${isClosing ? 'modal-backdrop--closing' : ''}`}
      onClick={() => handleClose()}
    >
      <div
        className={`modal-backdrop__content${
          staticContentEntry ? ' modal-backdrop__content--static' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {typeof children === 'function' ? children(handleClose) : children}
      </div>
    </div>
  )
}

export default ModalBackdrop